import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import {
  getPromotionById,
  deletePromotion,
  updatePromotion,
} from "../../api/promotion.api";
import { toast } from "react-toastify";
import { titleCase } from "../../utils/case";
import { hasRole } from "../../utils/auth";

const PromotionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canManagePromotions = hasRole("superadmin", "finance");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [record, setRecord] = useState(null);

  const parseJsonField = (value, fallback = {}) => {
    if (!value) return fallback;

    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return fallback;
      }
    }

    return value;
  };

  const fetchPromotion = async () => {
    try {
      setLoading(true);

      const data = await getPromotionById(id);
      setRecord(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotion();
  }, [id]);

  if (loading) {
    return <div className="text-center mt-5">{titleCase(t("loading"))}...</div>;
  }

  if (!record) {
    return (
      <div className="text-center mt-5">
        {titleCase(t("promotion not found"))}
      </div>
    );
  }

  const formula = parseJsonField(record.formula);
  const rules = parseJsonField(record.rules);
  const usage = rules.usage || {};

  const promotionItems = record.items || record.promotionItems || [];

  const packageItems = promotionItems.filter(
    (item) => item.itemType === "package"
  );

  const lotItems = promotionItems.filter(
    (item) => item.itemType === "lot"
  );

  const inventoryItems = promotionItems.filter(
    (item) => item.itemType === "add_on"
  );

  const getBannerUrl = () => {
    if (!record.bannerImage) return null;

    if (record.bannerImage.startsWith("http")) {
      return record.bannerImage;
    }

    return `${process.env.REACT_APP_API_URL}/${record.bannerImage}`;
  };

  const bannerUrl = getBannerUrl();

  const formatAmount = (value) => {
    if (value === undefined || value === null || value === "") return "-";

    return `RM ${Number(value).toFixed(2)}`;
  };

  const formatDiscount = () => {
    if (formula.discountType === "percentage") {
      return `${Number(formula.discountValue || 0)}%`;
    }

    return formatAmount(formula.discountValue);
  };

  const getMinQty = (items) => {
    return items?.[0]?.minimumQuantity ?? "-";
  };

  const getItemRecord = (item) => {
    if (item.itemType === "package") return item.package;
    if (item.itemType === "lot") return item.lot;
    if (item.itemType === "add_on") return item.addOn;
    return null;
  };

  const renderPromotionItemList = (
    items = [],
    renderLabel = (item) => item?.name
  ) => {
    if (!items.length) return <span>-</span>;

    return (
      <ul className="mb-0 ps-3">
        {items.map((item) => {
          const relatedRecord = getItemRecord(item);

          return (
            <li key={item.id}>
              {relatedRecord ? renderLabel(relatedRecord) : "-"}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("promotions"))}
        title={titleCase(t("view promotion"))}
        mainTitle={
          <div className="d-flex align-items-center gap-2">
            <span>{record.promoCode}</span>
            <Badge color={record.status === "active" ? "success" : "dark"}>
              {titleCase(t(record.status || "inactive"))}
            </Badge>
          </div>
        }
      />

      <Container fluid>
        <Row className="mb-4">
          <Col className="d-flex justify-content-end gap-2 flex-wrap">
            {canManagePromotions && (
              <>
                <button
                  className={`btn btn-sm ${
                    record.status === "active" ? "btn-warning" : "btn-success"
                  }`}
                  onClick={async () => {
                    const nextStatus =
                      record.status === "active" ? "inactive" : "active";

                    const confirmed = window.confirm(
                      nextStatus === "inactive"
                        ? t("are you sure you want to deactivate this promotion?")
                        : t("are you sure you want to activate this promotion?")
                    );

                    if (!confirmed) return;

                    try {
                      await updatePromotion(id, {
                        status: nextStatus,
                      });

                      toast.success(
                        nextStatus === "inactive"
                          ? t("promotion deactivated successfully")
                          : t("promotion activated successfully")
                      );

                      fetchPromotion();
                    } catch (err) {
                      toast.error(err.message);
                    }
                  }}
                >
                  {record.status === "active"
                    ? titleCase(t("deactivate"))
                    : titleCase(t("activate"))}
                </button>

                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => navigate(`/promotions/edit/${id}`)}
                >
                  {titleCase(t("edit"))}
                </button>

                <button
                  className="btn btn-sm btn-danger"
                  disabled={deleting}
                  onClick={async () => {
                    const confirmed = window.confirm(
                      t("are you sure you want to delete this promotion?")
                    );

                    if (!confirmed) return;

                    try {
                      setDeleting(true);

                      await deletePromotion(id);

                      toast.success(t("deleted successfully"));
                      navigate("/promotions");
                    } catch (err) {
                      toast.error(err.message);
                    } finally {
                      setDeleting(false);
                    }
                  }}
                >
                  {deleting ? t("deleting...") : titleCase(t("delete"))}
                </button>
              </>
            )}

            <button className="btn btn-sm btn-dark" onClick={() => navigate(-1)}>
              {titleCase(t("back"))}
            </button>
          </Col>
        </Row>

        <Row className="mb-4 g-3">
          <Col md="6">
            <div className="border rounded p-3 h-100">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1">{titleCase(t("total usage"))}</p>
                  <h4 className="mb-0">{record.usageCount ?? 0}</h4>
                </div>
                <div className="text-primary"><i className="fa fa-ticket-alt fa-2x" /></div>
              </div>
            </div>
          </Col>

          <Col md="6">
            <div className="border rounded p-3 h-100">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1">{titleCase(t("total amount discounted"))}</p>
                  <h4 className="mb-0">{formatAmount(record.totalDiscounted ?? 0)}</h4>
                </div>
                <div className="text-success"><i className="fa fa-money-bill-wave fa-2x" /></div>
              </div>
            </div>
          </Col>
        </Row>


        <Row className="mb-4">
          <Col md="6">
            <Card className="h-100 shadow-sm">
              <CardHeader>
                <H5>{titleCase(t("promotion details"))}</H5>
              </CardHeader>

              <CardBody>
                {bannerUrl && (
                  <div className="mb-3">
                    <img
                      src={bannerUrl}
                      alt="promotion banner"
                      style={{
                        width: "100%",
                        maxHeight: "220px",
                        objectFit: "cover",
                        borderRadius: "6px",
                      }}
                    />
                  </div>
                )}

                <div className="p-3 border rounded mb-3">
                  <p className="text-muted mb-1">{titleCase(t("promo code"))}</p>
                  <h6 className="mb-0">{record.promoCode || "-"}</h6>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md="6">
            <Card className="h-100 shadow-sm">
              <CardHeader>
                <H5>{titleCase(t("schedule"))}</H5>
              </CardHeader>

              <CardBody>
                <div className="p-3 border rounded mb-3">
                  <p className="text-muted mb-1">{titleCase(t("effective date"))}</p>
                  <h6 className="mb-0">{record.startDate || "-"}</h6>
                </div>

                <div className="p-3 border rounded mb-3">
                  <p className="text-muted mb-1">
                    {titleCase(t("always available"))}
                  </p>
                  <h6 className="mb-0">
                    {record.noExpiry ? titleCase(t("yes")) : titleCase(t("no"))}
                  </h6>
                </div>

                {!record.noExpiry && (
                  <div className="p-3 border rounded">
                    <p className="text-muted mb-1">{titleCase(t("end date"))}</p>
                    <h6 className="mb-0">{record.endDate || "-"}</h6>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md="6">
            <Card className="h-100 shadow-sm">
              <CardHeader>
                <H5>{titleCase(t("usage limits"))}</H5>
              </CardHeader>

              <CardBody>
                <div className="p-3 border rounded mb-3">
                  <p className="text-muted mb-1">
                    {titleCase(t("total redemption limit"))}
                  </p>
                  <h6 className="mb-0">
                    {usage.totalUsageLimit
                      ? usage.totalUsageLimit
                      : titleCase(t("unlimited"))}
                  </h6>
                </div>

                <div className="p-3 border rounded mb-3">
                  <p className="text-muted mb-1">
                    {titleCase(t("redemption limit per customer"))}
                  </p>
                  <h6 className="mb-0">
                    {usage.perCustomerLimit
                      ? usage.perCustomerLimit
                      : titleCase(t("unlimited"))}
                  </h6>
                </div>

                <div className="p-3 border rounded">
                  <p className="text-muted mb-1">
                    {titleCase(t("allow stacking with other promotions"))}
                  </p>
                  <h6 className="mb-0">
                    {usage.isStackable ? titleCase(t("yes")) : titleCase(t("no"))}
                  </h6>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md="6">
            <Card className="h-100 shadow-sm">
              <CardHeader>
                <H5>{titleCase(t("discount configuration"))}</H5>
              </CardHeader>

              <CardBody>
                <Row className="g-3 mb-3">
                  <Col xs="6">
                    <div className="p-3 border rounded h-100">
                      <p className="text-muted mb-1">{titleCase(t("minimum eligible spend"))}</p>
                      <h6 className="mb-0">{formatAmount(formula.minimumSpend)}</h6>
                    </div>
                  </Col>

                  <Col xs="6">
                    <div className="p-3 border rounded h-100">
                      <p className="text-muted mb-1">{titleCase(t("maximum discount cap"))}</p>
                      <h6 className="mb-0">
                        {formula.discountType === "percentage" ? formatAmount(formula.discountCap) : "-"}
                      </h6>
                    </div>
                  </Col>
                </Row>

                <Row className="g-3 mb-3">
                  <Col xs="6">
                    <div className="p-3 border rounded h-100">
                      <p className="text-muted mb-1">{titleCase(t("discount type"))}</p>
                      <h6 className="mb-0">
                        {titleCase(t((formula.discountType || "-").replace(/_/g, " ")))}
                      </h6>
                    </div>
                  </Col>

                  <Col xs="6">
                    <div className="p-3 border rounded h-100">
                      <p className="text-muted mb-1">{titleCase(t("applied by"))}</p>
                      <h6 className="mb-0">
                        {titleCase(t((formula.appliedBy || "-").replace(/_/g, " ")))}
                      </h6>
                    </div>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col xs="6">
                    <div className="p-3 border rounded h-100">
                      <p className="text-muted mb-1">{titleCase(t("standard discount value"))}</p>
                      <h6 className="mb-0">
                        {formula.discountType === "percentage"
                          ? `${Number(formula.discountValue?.standard ?? 0)}%`
                          : formatAmount(formula.discountValue?.standard)}
                      </h6>
                    </div>
                  </Col>

                  <Col xs="6">
                    <div className="p-3 border rounded h-100">
                      <p className="text-muted mb-1">{titleCase(t("peak discount value"))}</p>
                      <h6 className="mb-0">
                        {formula.discountType === "percentage"
                          ? `${Number(formula.discountValue?.peak ?? 0)}%`
                          : formatAmount(formula.discountValue?.peak)}
                      </h6>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <H5>{titleCase(t("applicability rules"))}</H5>
          </CardHeader>

          <CardBody>
            <Table bordered responsive>
              <thead>
                <tr>
                  <th>{titleCase(t("item type"))}</th>
                  <th>{titleCase(t("enabled"))}</th>
                  <th>{titleCase(t("selected items"))}</th>
                  <th>{titleCase(t("minimum quantity"))}</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>{titleCase(t("packages"))}</td>
                  <td>
                    <Badge color={packageItems.length > 0 ? "success" : "dark"}>
                      {packageItems.length > 0
                        ? titleCase(t("yes"))
                        : titleCase(t("no"))}
                    </Badge>
                  </td>
                  <td>{renderPromotionItemList(packageItems)}</td>
                  <td>{getMinQty(packageItems)}</td>
                </tr>

                <tr>
                  <td>{titleCase(t("camp lots"))}</td>
                  <td>
                    <Badge color={lotItems.length > 0 ? "success" : "dark"}>
                      {lotItems.length > 0
                        ? titleCase(t("yes"))
                        : titleCase(t("no"))}
                    </Badge>
                  </td>
                  <td>
                    {renderPromotionItemList(
                      lotItems,
                      (item) =>
                        `${item.name}${item.location ? ` (${item.location})` : ""}`
                    )}
                  </td>
                  <td>{getMinQty(lotItems)}</td>
                </tr>

                <tr>
                  <td>{titleCase(t("inventory"))}</td>
                  <td>
                    <Badge color={inventoryItems.length > 0 ? "success" : "dark"}>
                      {inventoryItems.length > 0
                        ? titleCase(t("yes"))
                        : titleCase(t("no"))}
                    </Badge>
                  </td>
                  <td>
                    {renderPromotionItemList(
                      inventoryItems,
                      (item) =>
                        `${item.name}${item.category ? ` (${item.category})` : ""}`
                    )}
                  </td>
                  <td>{getMinQty(inventoryItems)}</td>
                </tr>
              </tbody>
            </Table>
          </CardBody>
        </Card>

        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <H5>{titleCase(t("terms and conditions"))}</H5>
          </CardHeader>

          <CardBody>
            {record.termsAndConditions ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: record.termsAndConditions,
                }}
              />
            ) : (
              <p className="mb-0 text-muted">-</p>
            )}
          </CardBody>
        </Card>
      </Container>
    </Fragment>
  );
};

export default PromotionView;